import { useState, useEffect } from 'react';
import { Table, Input, Row, Col, Badge, Spinner, Button } from 'reactstrap';
import { RefreshCw, Eye, Trash2, Check } from 'react-feather';
import { toast } from 'react-toastify';
import { Btn, H5 } from '../../../AbstractElements';
import NotificationService from '../../../Api/services/notification.service';
import { useTranslation } from 'react-i18next';
import type { components } from '../../../Api/types/openapi';
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';

type NotificationResponse = components['schemas']['NotificationResponse'];
type PaginationNotificationResponse = components['schemas']['Pagination_NotificationResponse_'];

const NotificationsList = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<NotificationResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [readFilter, setReadFilter] = useState<string>('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    pages: 0,
    for_page: 0,
  });

  useEffect(() => {
    loadNotifications();
  }, [currentPage, searchTerm, readFilter]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await NotificationService.getNotificationsPaginated({
        page: currentPage,
        limit,
        search: searchTerm || undefined,
        read: readFilter || undefined,
      });
      
      setNotifications(data.results || []);
      setPagination({
        current_page: data.current_page || 1,
        pages: data.pages || 0,
        for_page: data.for_page || 0,
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al cargar notificaciones');
      toast.error(errorMessage);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: number) => {
    try {
      await NotificationService.markAsRead(id);
      toast.success(t('Notificación marcada como leída'));
      loadNotifications();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al marcar notificación');
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('¿Está seguro de que desea eliminar esta notificación?'))) {
      return;
    }

    try {
      await NotificationService.deleteNotification(id);
      toast.success(t('Notificación eliminada exitosamente'));
      loadNotifications();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al eliminar notificación');
      toast.error(errorMessage);
    }
  };

  const handleRefresh = () => {
    loadNotifications();
  };

  const getTypeBadge = (type: string | null) => {
    if (!type) return <Badge color="secondary">-</Badge>;
    
    switch (type) {
      case 'N':
        return <Badge color="info">{t('Normal')}</Badge>;
      case 'P':
        return <Badge color="success">{t('Pago')}</Badge>;
      case 'C':
        return <Badge color="primary">{t('Consulta Legal')}</Badge>;
      default:
        return <Badge color="secondary">{type}</Badge>;
    }
  };

  const renderPagination = () => {
    if (pagination.pages <= 1) return null;

    const pages = [];
    for (let i = 1; i <= pagination.pages; i++) {
      pages.push(
        <PaginationItem key={i} active={i === currentPage}>
          <PaginationLink onClick={() => setCurrentPage(i)}>
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return (
      <Pagination className="d-flex justify-content-center mt-3">
        <PaginationItem disabled={currentPage === 1}>
          <PaginationLink previous onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} />
        </PaginationItem>
        {pages}
        <PaginationItem disabled={currentPage === pagination.pages}>
          <PaginationLink next onClick={() => setCurrentPage(Math.min(pagination.pages, currentPage + 1))} />
        </PaginationItem>
      </Pagination>
    );
  };

  return (
    <div>
      <Row className="mb-3">
        <Col md="12">
          <div className="d-flex justify-content-between align-items-center">
            <H5 className="mb-0">{t('Mis Notificaciones')}</H5>
            <Btn color="secondary" onClick={handleRefresh} className="me-2">
              <RefreshCw size={16} className="me-1" />
              {t('Actualizar')}
            </Btn>
          </div>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md="6">
          <Input
            type="text"
            placeholder={t('Buscar notificaciones...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                setCurrentPage(1);
                loadNotifications();
              }
            }}
          />
        </Col>
        <Col md="6">
          <Input
            type="select"
            value={readFilter}
            onChange={(e) => {
              setReadFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">{t('Todas')}</option>
            <option value="true">{t('Leídas')}</option>
            <option value="false">{t('No Leídas')}</option>
          </Input>
        </Col>
      </Row>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner color="primary" />
        </div>
      ) : (
        <Table hover responsive>
          <thead>
            <tr>
              <th>{t('Título')}</th>
              <th>{t('Descripción')}</th>
              <th>{t('Tipo')}</th>
              <th>{t('Estado')}</th>
              <th>{t('Fecha')}</th>
              <th>{t('Acciones')}</th>
            </tr>
          </thead>
          <tbody>
            {notifications.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center">
                  {t('No se encontraron notificaciones')}
                </td>
              </tr>
            ) : (
              notifications.map((notification) => (
                <tr key={notification.id} className={!notification.read ? 'table-warning' : ''}>
                  <td>{notification.title || '-'}</td>
                  <td>{notification.description || '-'}</td>
                  <td>{getTypeBadge(notification.type_notification)}</td>
                  <td>
                    {notification.read ? (
                      <Badge color="success">{t('Leída')}</Badge>
                    ) : (
                      <Badge color="warning">{t('No Leída')}</Badge>
                    )}
                  </td>
                  <td>
                    {notification.created_at
                      ? new Date(notification.created_at).toLocaleDateString('es-ES', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '-'}
                  </td>
                  <td>
                    {!notification.read && (
                      <Btn
                        color="success"
                        size="sm"
                        className="me-1 btn_list_action"
                        onClick={() => notification.id && handleMarkAsRead(notification.id)}
                        title={t('Marcar como leída')}
                      >
                        <Check size={14} />
                      </Btn>
                    )}
                    <Btn
                      color="danger"
                      size="sm"
                      className="btn_list_action"
                      onClick={() => notification.id && handleDelete(notification.id)}
                      title={t('Eliminar')}
                    >
                      <Trash2 size={14} />
                    </Btn>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {renderPagination()}
    </div>
  );
};

export default NotificationsList;

